import { CONTACT_INFORMATION_TAG_NAME } from '@/shared-project';
import {
  isMobileAppQuestion,
  isUrlQuestionText,
  OPTION_RENDER_TYPES,
  QUESTION_SHAPES,
  type OptionRenderType,
  type QuestionShape,
} from '@/shared-question-model';
import { RESPONSE_MODES, type ResponseMode } from '@/shared-responder-v2-core';

import type { ResponderQuestionInput } from './shared-responder-v2-project.types';

type QuestionTypeLiteral =
  | 'Message'
  | 'MultipleChoice'
  | 'OpenEnded'
  | 'Ranking'
  | 'Rating'
  | 'Task';

type OptionRenderTypeLiteral = 'Emoji' | 'Media' | 'Star' | 'Text';

type InteractionTypeLiteral = 'Text' | 'Voice';

/**
 * Structural on purpose, and schema-free: `type`/`optionRenderType`/
 * `interactionType` are typed on the literal strings the manager and survey
 * `QuestionType`/`OptionRenderType`/`InteractionType` enums share, not either
 * generated enum. A string enum's member type is a subtype of a literal
 * union only while it carries exactly that union's members, so passing
 * either schema's enum value here type-checks today and a schema gaining a
 * new member becomes a compile error at the adapter's own call site — not a
 * cast, and not a runtime miss.
 */
export interface ResponderQuestionSource {
  readonly id: string;
  readonly text: string;
  readonly questionOrder: number;
  readonly type: QuestionTypeLiteral;
  readonly optionRenderType: OptionRenderTypeLiteral;
  readonly interactionType?: InteractionTypeLiteral | null;
  readonly required?: boolean | null;
  readonly randomize?: boolean | null;
  readonly minSelections?: number | null;
  readonly maxSelections?: number | null;
  readonly showLiveDistribution?: boolean | null;
  readonly aiConvoDepth?: number | null;
  readonly contentUrl?: string | null;
  readonly mobileAppEmbedUrl?: string | null;
  readonly notApplicableLabel?: string | null;
  readonly voiceText?: string | null;
  readonly isContactInfo?: boolean | null;
  readonly tags?: readonly { readonly name: string }[] | null;
  readonly assets?:
    | readonly { readonly url: string; readonly name?: string | null }[]
    | null;
  readonly options?:
    | readonly {
        readonly label: string;
        readonly value: string;
        readonly assets?: readonly { readonly url: string }[] | null;
      }[]
    | null;
  readonly subQuestions?:
    | readonly {
        readonly id: string;
        readonly text: string;
        readonly questionOrder: number;
      }[]
    | null;
}

function toQuestionShape(
  type: QuestionTypeLiteral,
  isUrl: boolean,
  isMobileApp: boolean
): QuestionShape {
  switch (type) {
    case 'Message':
      if (isMobileApp) return QUESTION_SHAPES.MobileApp;
      return isUrl ? QUESTION_SHAPES.Url : QUESTION_SHAPES.Message;
    case 'MultipleChoice':
      return QUESTION_SHAPES.MultipleChoice;
    case 'OpenEnded':
      return QUESTION_SHAPES.OpenEnded;
    case 'Ranking':
      return QUESTION_SHAPES.Ranking;
    case 'Rating':
      return QUESTION_SHAPES.Rating;
    case 'Task':
      return QUESTION_SHAPES.Task;
    default: {
      const unexpected: never = type;
      throw new Error('Unexpected question type: ' + String(unexpected));
    }
  }
}

function toSharedOptionRenderType(
  optionRenderType: OptionRenderTypeLiteral
): OptionRenderType {
  switch (optionRenderType) {
    case 'Media':
      return OPTION_RENDER_TYPES.Media;
    case 'Emoji':
      return OPTION_RENDER_TYPES.Emoji;
    case 'Star':
      return OPTION_RENDER_TYPES.Star;
    case 'Text':
      return OPTION_RENDER_TYPES.Text;
    default: {
      const unexpected: never = optionRenderType;
      throw new Error('Unexpected option render type: ' + String(unexpected));
    }
  }
}

/**
 * `InteractionType` (`Text` | `Voice`) is identical in meaning to
 * `RESPONSE_MODES`, just capitalised differently — this is the explicit
 * mapping between the two rather than a cast.
 */
export function toResponseMode(
  interactionType: InteractionTypeLiteral | null | undefined
): ResponseMode | null {
  if (interactionType == null) {
    return null;
  }

  switch (interactionType) {
    case 'Text':
      return RESPONSE_MODES.Text;
    case 'Voice':
      return RESPONSE_MODES.Voice;
    default: {
      const unexpected: never = interactionType;
      throw new Error('Unexpected interaction type: ' + String(unexpected));
    }
  }
}

function resolveIsContactInfo(question: ResponderQuestionSource): boolean {
  if (question.isContactInfo != null) {
    return question.isContactInfo;
  }

  return (
    question.tags?.some((tag) => tag.name === CONTACT_INFORMATION_TAG_NAME) ??
    false
  );
}

/**
 * Turns one manager or survey question into the schema-free shape the
 * session builder reads. Everything schema-shaped stops at the caller: the
 * adapter's own enum types, the contact-info tag lookup input, and the
 * option/sub-question field names all arrive already named the way this
 * reads them.
 */
export function toResponderQuestionInput(
  question: ResponderQuestionSource
): ResponderQuestionInput {
  return {
    id: question.id,
    text: question.text,
    questionOrder: question.questionOrder,
    questionShape: toQuestionShape(
      question.type,
      isUrlQuestionText(question.text),
      isMobileAppQuestion(question)
    ),
    optionRenderType: toSharedOptionRenderType(question.optionRenderType),
    isContactInfo: resolveIsContactInfo(question),
    interactionMode: toResponseMode(question.interactionType),
    required: question.required,
    randomize: question.randomize,
    minSelections: question.minSelections,
    maxSelections: question.maxSelections,
    showLiveDistribution: question.showLiveDistribution,
    aiConvoDepth: question.aiConvoDepth,
    contentUrl: question.contentUrl,
    mobileAppEmbedUrl: question.mobileAppEmbedUrl,
    notApplicableLabel: question.notApplicableLabel,
    voiceText: isUrlQuestionText(question.text) ? null : question.voiceText,
    assets: question.assets?.map((asset) => ({
      url: asset.url,
      name: asset.name,
    })),
    options: question.options?.map((option) => ({
      label: option.label,
      value: option.value,
      assets: option.assets?.map((asset) => ({ url: asset.url })),
    })),
    subQuestions: question.subQuestions?.map((subQuestion) => ({
      id: subQuestion.id,
      text: subQuestion.text,
      questionOrder: subQuestion.questionOrder,
    })),
  };
}
